import React, { useEffect, useRef, useState } from "react";
import { adminApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
    Upload,
    Trash2,
    FileText,
    CheckCircle2,
    AlertCircle,
    Loader2,
    XCircle,
    RefreshCw,
} from "lucide-react";

const MAX_BYTES = 500 * 1024 * 1024; // keep in sync with backend
const CHUNK_SIZE = 5 * 1024 * 1024; // upload in 5 MB chunks to bypass proxy/CDN body-size limits
const ALLOWED = [
    ".pdf",
    ".docx",
    ".txt",
    ".md",
    ".png",
    ".jpg",
    ".jpeg",
    ".webp",
    ".tif",
    ".tiff",
    ".bmp",
];

function fmtSize(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function Documents() {
    const [items, setItems] = useState([]);
    const [queue, setQueue] = useState([]); // [{id, file, status, error}]
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);

    const load = () =>
        adminApi.get("/api/admin/documents").then((r) => setItems(r.data.items));

    useEffect(() => {
        load();
    }, []);

    // Poll every 5s while ANY doc is still indexing — stop when all settle.
    useEffect(() => {
        const hasIndexing = items.some((d) => d.status === "indexing");
        if (!hasIndexing) return;
        const id = setInterval(load, 5000);
        return () => clearInterval(id);
    }, [items]);

    const validateFile = (file) => {
        const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
        if (!ALLOWED.includes(ext)) {
            return `Unsupported file type ${ext}. Allowed: ${ALLOWED.join(", ")}`;
        }
        if (file.size > MAX_BYTES) {
            return `File exceeds the ${MAX_BYTES / 1024 / 1024} MB limit (${fmtSize(file.size)})`;
        }
        return null;
    };

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const describeError = (e) => {
        if (e.response) {
            const d = e.response.data?.detail;
            if (typeof d === "string") return d;
            return `Server error (HTTP ${e.response.status}). Please retry.`;
        }
        if (e.code === "ECONNABORTED")
            return "Upload timed out — the server may be busy. It was retried automatically; please retry once more.";
        return "Network error — the connection dropped during upload. Please retry.";
    };

    // Retries a request up to 3 times on network/5xx errors. Validation errors
    // (4xx) surface immediately and are NOT retried.
    const postWithRetry = async (fn, onAttempt) => {
        const MAX_ATTEMPTS = 3;
        let lastErr;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                return await fn();
            } catch (e) {
                lastErr = e;
                const status = e.response?.status;
                if (status && status >= 400 && status < 500) throw e;
                if (attempt < MAX_ATTEMPTS) {
                    onAttempt?.(attempt + 1);
                    await sleep(attempt * 2000);
                }
            }
        }
        throw lastErr;
    };

    // Chunked upload: split the file into small parts and stream them one at a
    // time. Each request stays well under any proxy/CDN body-size limit (e.g.
    // Cloudflare's 100 MB cap), so large files no longer drop mid-upload.
    const uploadOne = async (file, onAttempt, onProgress) => {
        const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
        const initRes = await postWithRetry(() =>
            adminApi.post("/api/admin/documents/upload/init", {
                filename: file.name,
                size: file.size,
                total_chunks: total,
            })
        );
        const uploadId = initRes.data.upload_id;

        for (let i = 0; i < total; i++) {
            const blob = file.slice(i * CHUNK_SIZE, i * CHUNK_SIZE + CHUNK_SIZE);
            await postWithRetry(() => {
                const fd = new FormData();
                fd.append("upload_id", uploadId);
                fd.append("chunk_index", String(i));
                fd.append("chunk", blob, file.name);
                return adminApi.post("/api/admin/documents/upload/chunk", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 120_000,
                });
            }, onAttempt);
            onProgress?.(i + 1, total);
        }

        await postWithRetry(() =>
            adminApi.post("/api/admin/documents/upload/complete", {
                upload_id: uploadId,
            })
        );
    };

    const enqueueFiles = async (files) => {
        const list = Array.from(files || []);
        if (list.length === 0) return;

        const initial = list.map((f, i) => {
            const err = validateFile(f);
            return {
                id: `${Date.now()}-${i}-${f.name}`,
                file: f,
                status: err ? "rejected" : "queued",
                error: err,
            };
        });
        setQueue((q) => [...q, ...initial]);

        setUploading(true);
        for (const item of initial) {
            if (item.status === "rejected") continue;
            setQueue((q) =>
                q.map((it) => (it.id === item.id ? { ...it, status: "uploading", attempt: 1 } : it))
            );
            try {
                await uploadOne(
                    item.file,
                    (attempt) =>
                        setQueue((q) =>
                            q.map((it) => (it.id === item.id ? { ...it, attempt } : it))
                        ),
                    (done, tot) =>
                        setQueue((q) =>
                            q.map((it) =>
                                it.id === item.id
                                    ? { ...it, pct: Math.round((done / tot) * 100) }
                                    : it
                            )
                        )
                );
                setQueue((q) =>
                    q.map((it) => (it.id === item.id ? { ...it, status: "done" } : it))
                );
            } catch (e) {
                const message = describeError(e);
                setQueue((q) =>
                    q.map((it) =>
                        it.id === item.id
                            ? { ...it, status: "failed", error: message }
                            : it
                    )
                );
            }
        }
        setUploading(false);
        await load();
    };

    const retryItem = async (item) => {
        setQueue((q) =>
            q.map((it) =>
                it.id === item.id
                    ? { ...it, status: "uploading", error: null, attempt: 1 }
                    : it
            )
        );
        try {
            await uploadOne(
                item.file,
                (attempt) =>
                    setQueue((q) =>
                        q.map((it) => (it.id === item.id ? { ...it, attempt } : it))
                    ),
                (done, tot) =>
                    setQueue((q) =>
                        q.map((it) =>
                            it.id === item.id
                                ? { ...it, pct: Math.round((done / tot) * 100) }
                                : it
                        )
                    )
            );
            setQueue((q) =>
                q.map((it) => (it.id === item.id ? { ...it, status: "done" } : it))
            );
        } catch (e) {
            const message = describeError(e);
            setQueue((q) =>
                q.map((it) =>
                    it.id === item.id ? { ...it, status: "failed", error: message } : it
                )
            );
        }
        await load();
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDrag(false);
        enqueueFiles(e.dataTransfer.files);
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this document and its indexed chunks?")) return;
        await adminApi.delete(`/api/admin/documents/${id}`);
        load();
    };

    const onReindex = async (id) => {
        try {
            await adminApi.post(`/api/admin/documents/${id}/reindex`);
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            window.alert(
                typeof d === "string" ? d : "Could not re-index this document. Please retry."
            );
        }
    };

    const dismissQueueItem = (id) =>
        setQueue((q) => q.filter((it) => it.id !== id));
    const clearCompleted = () =>
        setQueue((q) => q.filter((it) => !["done", "rejected"].includes(it.status)));

    const completedCount = queue.filter((q) => q.status === "done").length;
    const totalActive = queue.filter((q) => q.status !== "rejected").length;

    return (
        <div className="p-10 max-w-7xl" data-testid="page-documents">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                    Knowledge Base
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Documents</h1>
                <p className="text-stone-600 mt-2 max-w-2xl">
                    Upload property guides, policy docs, menus, FAQ sheets — the Concierge will
                    learn from them to answer guests intelligently.
                </p>
                <div
                    className="mt-4 flex flex-wrap items-center gap-2 text-xs text-stone-500"
                    data-testid="two-step-explainer"
                >
                    <span className="inline-flex items-center gap-1.5 bg-orchid-700/5 text-orchid-700 rounded-full px-3 py-1">
                        <span className="font-semibold">Step 1</span> Upload — file is sent &amp; saved on the server
                    </span>
                    <span className="text-stone-300">→</span>
                    <span className="inline-flex items-center gap-1.5 bg-gold-700/5 text-gold-700 rounded-full px-3 py-1">
                        <span className="font-semibold">Step 2</span> Server reads it &amp; teaches the AI (track below)
                    </span>
                </div>
            </div>

            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                data-testid="upload-dropzone"
                className={`mb-6 border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                    drag
                        ? "border-orchid-700 bg-orchid-700/5"
                        : "border-stone-300 hover:border-orchid-700/40 bg-white"
                }`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp,.tif,.tiff,.bmp"
                    className="hidden"
                    onChange={(e) => {
                        enqueueFiles(e.target.files);
                        e.target.value = ""; // allow re-selecting the same files
                    }}
                    data-testid="upload-input"
                />
                <div className="flex justify-center mb-3">
                    {uploading ? (
                        <Loader2 size={36} className="text-orchid-700 animate-spin" />
                    ) : (
                        <Upload size={36} className="text-orchid-700/70" />
                    )}
                </div>
                <div className="font-heading text-2xl text-orchid-700 mb-1">
                    {uploading
                        ? `Indexing ${completedCount + 1} of ${totalActive}…`
                        : "Drag & drop, or click to upload"}
                </div>
                <div className="text-sm text-stone-500">
                    PDF, DOCX, TXT, MD, images (PNG/JPG/WEBP/TIFF) · up to 500 MB · multiple files
                </div>
                <div className="text-[11px] text-stone-400 mt-1">
                    Scanned PDFs &amp; images are auto-OCRed (English + Hindi). Legacy .doc / .xls / .ppt — save as .docx first.
                </div>
            </div>

            {/* Upload queue */}
            {queue.length > 0 && (
                <div
                    className="bg-white border border-stone-200 rounded-lg p-4 mb-6"
                    data-testid="upload-queue"
                >
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-stone-900">
                            Upload queue · {completedCount}/{totalActive} done
                        </div>
                        {(queue.some((q) => q.status === "done") ||
                            queue.some((q) => q.status === "rejected")) && (
                            <button
                                onClick={clearCompleted}
                                className="text-xs text-stone-500 hover:text-orchid-700"
                            >
                                Clear completed
                            </button>
                        )}
                    </div>
                    <ul className="space-y-1.5">
                        {queue.map((it) => (
                            <li
                                key={it.id}
                                data-testid={`queue-item-${it.status}`}
                                className={`text-sm py-2 border-b border-stone-100 last:border-b-0 ${
                                    ["failed", "rejected"].includes(it.status)
                                        ? "bg-red-50/40 -mx-2 px-2 rounded"
                                        : ""
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <FileText size={14} className="text-stone-400 shrink-0" />
                                    <div className="flex-1 min-w-0 truncate">{it.file.name}</div>
                                    <div className="text-xs text-stone-400 shrink-0">
                                        {fmtSize(it.file.size)}
                                    </div>
                                    <div className="w-28 text-right">
                                        {it.status === "queued" && (
                                            <span className="text-xs text-stone-500">Queued</span>
                                        )}
                                        {it.status === "uploading" && (
                                            <span className="inline-flex items-center gap-1 text-xs text-orchid-700">
                                                <Loader2 size={12} className="animate-spin" />
                                                {it.attempt > 1
                                                    ? `Retry ${it.attempt}/3`
                                                    : it.pct != null
                                                    ? `Uploading ${it.pct}%`
                                                    : "Uploading"}
                                            </span>
                                        )}
                                        {it.status === "done" && (
                                            <span className="inline-flex items-center gap-1 text-xs text-sage-700">
                                                <CheckCircle2 size={12} /> Uploaded
                                            </span>
                                        )}
                                        {it.status === "failed" && (
                                            <span className="inline-flex items-center gap-1 text-xs text-red-700">
                                                <AlertCircle size={12} /> Failed
                                            </span>
                                        )}
                                        {it.status === "rejected" && (
                                            <span className="inline-flex items-center gap-1 text-xs text-red-700">
                                                <XCircle size={12} /> Skipped
                                            </span>
                                        )}
                                    </div>
                                    {it.status === "failed" && (
                                        <button
                                            onClick={() => retryItem(it)}
                                            data-testid={`queue-retry-${it.id}`}
                                            className="text-xs font-medium text-orchid-700 hover:text-orchid-900 border border-orchid-700/30 rounded px-2 py-0.5 ml-2 shrink-0"
                                        >
                                            Retry
                                        </button>
                                    )}
                                    <button
                                        onClick={() => dismissQueueItem(it.id)}
                                        className="text-stone-300 hover:text-stone-700 ml-2"
                                        aria-label="Dismiss"
                                    >
                                        <XCircle size={14} />
                                    </button>
                                </div>
                                {(it.status === "failed" || it.status === "rejected") && it.error && (
                                    <div
                                        className="text-[12px] text-red-700 mt-1 ml-7 leading-snug"
                                        data-testid={`queue-error-${it.id}`}
                                    >
                                        <strong>Why:</strong> {it.error}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="docs-table">
                    <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-[0.1em]">
                        <tr>
                            <th className="text-left px-5 py-3">File</th>
                            <th className="text-left px-5 py-3">Size</th>
                            <th className="text-left px-5 py-3">Chunks</th>
                            <th className="text-left px-5 py-3">Status</th>
                            <th className="text-left px-5 py-3">Indexed</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((d) => (
                            <React.Fragment key={d.id}>
                            <tr
                                className="border-t border-stone-100"
                                data-testid={`doc-${d.id}`}
                            >
                                <td className="px-5 py-3 flex items-center gap-2">
                                    <FileText size={15} className="text-orchid-700/70" />
                                    {d.filename}
                                </td>
                                <td className="px-5 py-3 text-stone-600">
                                    {fmtSize(d.size_bytes)}
                                </td>
                                <td className="px-5 py-3 text-stone-700">{d.chunk_count}</td>
                                <td className="px-5 py-3">
                                    {d.status === "indexed" ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-orchid-500">
                                            <CheckCircle2 size={13} /> Indexed
                                        </span>
                                    ) : d.status === "failed" ? (
                                        <span
                                            className="inline-flex items-center gap-1 text-xs text-red-700"
                                            title={d.error || "Indexing failed"}
                                        >
                                            <AlertCircle size={13} /> Failed
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-gold-700">
                                            <Loader2 size={13} className="animate-spin" /> Processing
                                            {d.progress ? (
                                                <span className="text-stone-400 normal-case">
                                                    · {d.progress}
                                                </span>
                                            ) : (
                                                <span className="text-stone-400 normal-case">
                                                    · server is reading &amp; learning…
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-3 text-stone-600 whitespace-nowrap">
                                    {formatDateTime(d.indexed_at || d.uploaded_at)}
                                </td>
                                <td className="px-5 py-3">
                                    <div className="flex items-center justify-end gap-1">
                                        {d.status !== "indexing" && (
                                            <button
                                                onClick={() => onReindex(d.id)}
                                                data-testid={`doc-reindex-${d.id}`}
                                                title="Re-run OCR & indexing on this file (no re-upload needed)"
                                                className="p-1.5 text-stone-400 hover:text-orchid-700 hover:bg-orchid-50 rounded"
                                            >
                                                <RefreshCw size={15} />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => onDelete(d.id)}
                                            data-testid={`doc-delete-${d.id}`}
                                            className="p-1.5 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            {d.status === "failed" && d.error && (
                                <tr
                                    key={`${d.id}-err`}
                                    className="bg-red-50/60"
                                    data-testid={`doc-error-${d.id}`}
                                >
                                    <td colSpan={6} className="px-5 py-2 text-xs text-red-800 border-t border-red-100">
                                        <strong>Why it failed:</strong> {d.error}
                                    </td>
                                </tr>
                            )}
                            </React.Fragment>
                        ))}
                        {items.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-stone-500 py-12">
                                    No documents yet. Upload your first one above.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
